import { DynamicEmsData, EmsBatteryMode, EmsSgReadyMode, PriceItem, ChartPriceItem } from 'src/Libs/dynamic-power-prices/types'
import { Resolution } from 'src/Libs/chart-kit/shared/types'
import { incrementDateByResolution } from 'src/Libs/chart-kit/shared/utils/csv-export-fns'
import { AI360DynamicEmsData } from '../types'
import { RechartDataObject } from 'src/Libs/chart-kit'
import { isAfter, isBefore } from '@e3dc-react/shell/Libs/date'

interface ConvertPriceDataProps<T extends PriceItem> {
  priceData: T[]
  from: Date | undefined
  to: Date | undefined
  resolution?: Resolution
}

/**
 * Generic function to convert any price data to a format suitable for the line chart.
 * data points are filled based on the provided time range and resolution.
 */
export const convertPriceData = <T extends PriceItem>(props: ConvertPriceDataProps<T>): ChartPriceItem[] => {
  const { priceData, from, to, resolution = '15min' } = props

  if (!from || !to) return []

  const data: ChartPriceItem[] = []
  let currentTime = new Date(from.valueOf())
  const end = new Date(to.valueOf())

  while (currentTime < end) {
    const time = new Date(currentTime)
    const { price } = getPriceAtTime(time, priceData)

    data.push({
      time: time.valueOf(),
      price,
    })
    currentTime = incrementDateByResolution(currentTime, resolution)
  }

  return data
}

/**
 * Returns the price for a given time.
 * If the price is not available, it checks if there is a valid price within the last hours.
 * If so, it returns the last valid price. Otherwise, it returns null.
 * This logic is relevant for the day view, where data is mixed with different resolutions.
 */

const getPriceAtTime = <T extends PriceItem>(time: Date, priceData: T[]): { price: number | null } => {
  const timeIso = time.toISOString()
  const priceItem = priceData.find(item => item.validFrom <= timeIso && item.validTo > timeIso)

  return { price: priceItem?.price ?? null }
}

/**
 * get solar production and forcast data from performance data.
 * if the date or time is in the past (ie. when we have solar production data), we don't show forecast data.
 */
export const getSolarAndForecastData = (
  performanceData: RechartDataObject,
  isDateInPast: boolean
): { solarProduction: number | null; solarForecast: number | null } => {
  const solarProduction = (performanceData?.['DC_PowerSolar'] as number) ?? null
  const forecastPower = (performanceData?.['ForecastPower'] as number) ?? null
  const hasSolarProductionData = solarProduction !== null
  /* we don't show forecast for previous days and for current day if there is actual solar production data */
  const dontShowForecastData = isDateInPast || hasSolarProductionData
  return {
    solarProduction: solarProduction,
    solarForecast: dontShowForecastData ? null : forecastPower,
  }
}

interface ConvertDynamicEmsDataProps {
  dynamicEmsData: DynamicEmsData[]
  from: Date | undefined
  to: Date | undefined
  resolution?: Resolution
}

/**
 * This function converts the dynamic EMS data into a format that can be used for the AI360 line chart.
 * currently ai360 uses a fixed 15-minute resolution, so the function fills in the data points based on this resolution and the provided time range.
 *
 */
export const convertDynamicEmsData = ({ dynamicEmsData, from, to, resolution = '15min' }: ConvertDynamicEmsDataProps): AI360DynamicEmsData[] => {
  if (!from || !to) return []

  const data: AI360DynamicEmsData[] = []
  let currentTime = new Date(from?.valueOf() ?? 0)
  const end = new Date(to?.valueOf() ?? 0)

  while (currentTime < end) {
    const time = new Date(currentTime)
    const { emsBatteryMode, emsSgReadyMode } = getDynamicEmsData(time, dynamicEmsData)

    data.push({
      time: time.valueOf(),
      emsBatteryMode: emsBatteryMode,
      emsSgReadyMode: emsSgReadyMode,
    })
    currentTime = incrementDateByResolution(currentTime, resolution)
  }

  return data
}

/**
 * This function is used to get the EMS data for a given time. It checks if there is a valid EMS data entry for the given time and returns the corresponding battery mode and sg ready mode. If there is no valid entry, it returns null for both modes.
 */
const getDynamicEmsData = (
  time: Date,
  dynamicEmsData: DynamicEmsData[]
): { emsBatteryMode: EmsBatteryMode | null; emsSgReadyMode: EmsSgReadyMode | null } => {
  const emsData = dynamicEmsData?.find(item => item.start <= time.toISOString() && item.end > time.toISOString())

  return { emsBatteryMode: emsData?.emsBatteryMode ?? null, emsSgReadyMode: emsData?.emsSgReadyMode ?? null }
}

/**
 * Remove price entries that are not in the requested time range.
 */
export const removeOutOfRangePrices = <T extends PriceItem>(prices: T[], from: Date, to: Date): T[] => {
  return prices.filter(price => {
    const validFrom = new Date(price.validFrom)
    const validTo = new Date(price.validTo)

    /* Keep entry only if its interval overlaps with [from, to): */
    return isBefore(validFrom, to) && isAfter(validTo, from)
  })
}
